import React, { useState, useEffect, useRef, useCallback } from "react";
import { Space, Table, Tag, Tooltip, Button } from "antd";
import { useNavigate } from "react-router-dom";
import useTableUrlState from "../utils/useTableUrlState";
import { toast } from "react-toastify";
import axios from "axios";
import Swal from "sweetalert2";
import { CopyOutlined } from "@ant-design/icons";
import qs from "qs";

const CampaignScheduler = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);
  // Page, page size and search live in the address, so Back keeps the view.
  const { page, pageSize, q, setPagination, setQuery } =
    useTableUrlState("campaign-scheduler");
  const [searchInput, setSearchInput] = useState(q);
  // Campaigns whose Trigger was clicked in this tab. The button turns into
  // "TRIGGERED" immediately, so a second click is impossible; the server also
  // refuses a second trigger (409) whichever worker or tab it comes from.
  const [triggered, setTriggered] = useState({});
  // Instant lock: state only updates on the next render, a ref blocks a
  // second click arriving in the same moment.
  const triggering = useRef(new Set());

  const handleTableChange = (pagination) => {
    setPagination(pagination.current, pagination.pageSize);
  };

  const requestSeq = useRef(0);
  const inFlight = useRef(false);

  // isPoll: the background refresh skips a round while a request is still
  // running, so requests never pile up when the server is slow.
  const fetchSchedulerData = useCallback(
    async (isPoll = false) => {
      if (isPoll && inFlight.current) return;
      const seq = ++requestSeq.current;
      inFlight.current = true;
      try {
        const params = {
          startreq: (page - 1) * pageSize,
          limit: pageSize,
          ...(q ? { q } : {}),
        };

        const response = await axios.get(
          `/api/campaignscheduler/get_campaign_scheduler?${qs.stringify(params)}`
        );
        if (seq !== requestSeq.current) return;

        setData(response.data.contacts);
        setTotal(response.data.totalCount);
        // Once the server shows a clicked campaign as triggered, the status
        // drives the button again (e.g. 🚀 returns if it is later cancelled).
        const serverTriggered = new Set(
          response.data.contacts
            .filter((c) => ["scheduled", "queued", "in_progress", "complete"].includes(c.allstatus))
            .map((c) => c._id)
        );
        setTriggered((prev) => {
          const ids = Object.keys(prev).filter((id) => !serverTriggered.has(id));
          return ids.length === Object.keys(prev).length
            ? prev
            : Object.fromEntries(ids.map((id) => [id, true]));
        });
      } catch (error) {
        console.error("Failed to fetch Scheduler Data:", error);
        if (!isPoll) {
          toast.error(
            error.response?.data?.message ||
              error.message ||
              "Failed to fetch scheduler data"
          );
        }
      } finally {
        if (seq === requestSeq.current) inFlight.current = false;
      }
    },
    [page, pageSize, q]
  );

  useEffect(() => {
    // Check if success message exists in sessionStorage
    const successMessage = sessionStorage.getItem("campaign_success");

    if (successMessage) {
      toast.success(successMessage);
      // Clear the stored message after showing the toast
      sessionStorage.removeItem("campaign_success");
    }
  }, []);

  // Load whenever the page, page size or search changes.
  useEffect(() => {
    fetchSchedulerData();
  }, [fetchSchedulerData]);

  // Pause switch: whether tmc may send right now, and from which server.
  const [sending, setSending] = useState(null);
  const fetchSending = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/campaignscheduler/sending-control");
      setSending(data);
    } catch (error) {
      console.error("Failed to read sending status:", error);
    }
  }, []);
  useEffect(() => {
    fetchSending();
    const interval = setInterval(() => {
      if (!document.hidden) fetchSending();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchSending]);

  const toggleSending = async () => {
    const pause = !sending?.paused;
    const confirm = await Swal.fire({
      title: pause ? "Pause all sending?" : "Resume sending?",
      text: pause
        ? "Campaigns that are sending stop within a few seconds and wait in the queue. Nothing new starts until you resume."
        : "Queued and scheduled campaigns start again. Campaigns more than 30 minutes past their time are not sent late.",
      icon: pause ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: pause ? "Pause sending" : "Resume sending",
    });
    if (!confirm.isConfirmed) return;
    try {
      const { data } = await axios.post("/api/campaignscheduler/sending-control", { paused: pause });
      setSending(data);
      toast.success(pause ? "Sending paused" : "Sending resumed");
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not change sending status");
    }
  };

  // Light refresh every 15 s, only while this tab is visible.
  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) fetchSchedulerData(true);
    };
    const interval = setInterval(refresh, 15000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [fetchSchedulerData]);

  // Search the whole database once typing pauses.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== q) setQuery(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, q, setQuery]);

  const handleCreateCampaign = () => {
    navigate("/dashboard/whatsapp_new"); // Redirect to the form page
  };

  const handleDeleteSchedulerData = async (_id) => {
    const confirmed = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirmed.isConfirmed) {
      Swal.fire({
        title: "Deleting...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      axios
        .delete(`/api/campaignscheduler/delete_campaign_scheduler/${_id}`)
        .then((res) => {
          fetchSchedulerData();
          Swal.fire({
            title: "Deleted!",
            text: "Data deleted successfully!",
            icon: "success",
            timer: 2000, // Auto-close after 2 seconds
            showConfirmButton: false,
          });
        })
        .catch((error) => {
          Swal.fire({
            title: "Error",
            text:
              error.response?.data?.message ||
              error.message ||
              "Failed to delete the campaign",
            icon: "error",
            timer: 2000, // Auto-close after 2 seconds
            showConfirmButton: false,
          });
        });
    }
  };

  const handleEditSchedulerData = async (_id) => {
    navigate(`/dashboard/whatsapp_edit/${_id}`);
  };

  const handleTriggerApi = async (_id) => {
    if (triggered[_id] || triggering.current.has(_id)) return;
    triggering.current.add(_id);
    setTriggered((prev) => ({ ...prev, [_id]: true }));

    try {
      const response = await axios.post(
        `/api/campaignscheduler/sendmessage/${_id}`
      );
      toast.success(response.data?.message || "Campaign triggered");
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info("This campaign has already been triggered.");
      } else {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to trigger campaign"
        );
        // Not triggered: allow another try.
        setTriggered((prev) => {
          const updated = { ...prev };
          delete updated[_id];
          return updated;
        });
      }
    } finally {
      triggering.current.delete(_id);
      fetchSchedulerData();
    }
  };

  const handleCancelApi = async (_id) => {
    const confirmCancel = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to cancel this campaign?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, cancel it!",
    });

    if (confirmCancel.isConfirmed) {
      try {
        const response = await axios.post(
          `/api/campaignscheduler/cancel/${_id}`
        );
        Swal.fire("Cancelled!", "The campaign has been cancelled.", "success");

        // Refresh the campaign list if needed
        // ✅ Refresh UI to reflect cancelled status and reset any sent/trigger state
        await fetchSchedulerData(); // <-- Make sure this updates your `campaigns` list
      } catch (error) {
        Swal.fire(
          "Error!",
          error.response?.data?.message || "Failed to cancel the campaign.",
          "error"
        );
      }
    }
  };


  const handleViewCampaign = (id, campaignId) => {
    navigate(`/dashboard/whatsappcloudreportdetails/${id}`, {
      state: { campaignId },
    }); // Redirect to the campaign card page
  };
  const handleViewFailedCampaign = (id, campaignId) => {
    navigate(`/dashboard/whatsappcloudreportfaileddetails/${id}`, {
      state: { campaignId },
    }); // Redirect to the campaign card page
  };

  const handleDuplicateWebinar = async (_id) => {
    navigate(`/dashboard/whatsapp_new_copy/${_id}`);
  };

  const columns = [
    {
      title: "ACTION",
      key: "action",
      align: "center",
      fixed: "left",
      width: 150,
      render: (text, record) => {
        const {
          _id,
          scheduledTime,
          allstatus,
          campaignName,
          lastResponseStatus,
        } = record;
        const renderButton = (
          icon,
          onClick,
          className = "edit-btn",
          disabled = false
        ) => (
          <button
            type="button"
            className={className}
            onClick={onClick}
            disabled={disabled}
          >
            <i className={icon}></i>
          </button>
        );

        const buttons = [];

        // if (["complete", "completed"].includes(allstatus)) {
        //   buttons.push(
        //     renderButton(
        //       "ri-eye-line",
        //       () => handleViewCampaign(_id, campaignName),
        //       "edit-btn"
        //     )
        //   );
        // }

        // whatsappResponses ka last status nikalna
        let lastStatus = "";
        if (lastResponseStatus) {
          lastStatus = lastResponseStatus;
        }

        if (["complete", "completed"].includes(allstatus)) {
          if (lastStatus === "Failed") {
            buttons.push(
              renderButton(
                "ri-eye-line",
                () => handleViewFailedCampaign(_id, campaignName),
                "edit-btn"
              )
            );
          } else if (lastStatus === "Sent" || !lastStatus) {
            buttons.push(
              renderButton(
                "ri-eye-line",
                () => handleViewCampaign(_id, campaignName),
                "edit-btn"
              )
            );
          }
        }

        if (["scheduled", "in_progress", "queued"].includes(allstatus)) {
          buttons.push(
            <Tag key="triggered" color="green">
              TRIGGERED
            </Tag>,
            renderButton("ri-close-line", () =>
              handleCancelApi(_id, scheduledTime)
            )
          );
        } else if (allstatus === "paused_balance") {
          // Out of wa.team balance: it retries by itself every 10 min; the
          // rocket tries now (e.g. right after a top-up).
          if (triggered[_id]) {
            buttons.push(
              <Tag key="triggered" color="green">
                TRIGGERED
              </Tag>
            );
          } else {
            buttons.push(
              <Tooltip key="retry" title="Balance topped up? Try now">
                {renderButton("ri-rocket-2-line", () => handleTriggerApi(_id))}
              </Tooltip>,
              renderButton("ri-close-line", () =>
                handleCancelApi(_id, scheduledTime)
              )
            );
          }
        } else if (
          ["cancelled", "partial_success", "pending", "rescheduled"].includes(
            allstatus
          )
        ) {
          if (triggered[_id]) {
            // Clicked in this tab: shown at once, before the next refresh.
            buttons.push(
              <Tag key="triggered" color="green">
                TRIGGERED
              </Tag>
            );
          } else {
            buttons.push(
              renderButton("ri-rocket-2-line", () => handleTriggerApi(_id)),
              renderButton("ri-pencil-fill", () => handleEditSchedulerData(_id))
            );
          }
        }

        buttons.push(
          renderButton(
            "ri-delete-bin-fill",
            () => handleDeleteSchedulerData(_id),
            "delete-btn"
          )
        );

        buttons.push(
          renderButton(
            "ri-file-copy-line",
            () => handleDuplicateWebinar(_id),
            "delete-btn"
          )
        );

        return <Space size="middle">{buttons}</Space>;
      },
    },
    // Channel
    {
      title: "Connection Name",
      dataIndex: "selectedChannelConnectionName",
      key: "selectedChannelConnectionName",
      align: "center",
      ellipsis: true,
    },
    {
      title: "CHANNEL",
      dataIndex: "campaignDetails",
      key: "campaignDetails",
      align: "center",
      ellipsis: true,
    },
    // Name
    {
      title: "NAME",
      dataIndex: "campaignName",
      key: "campaignName",
      align: "center",
      ellipsis: true,
    },
    // ID
    {
      title: "ID",
      dataIndex: "_id",
      key: "_id",
      align: "center",
      ellipsis: true,
      render: (text) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <span>{text.substring(0, 8)}...</span>

          <Tooltip title="Copy ID">
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(text);
                toast.success("Copied successfully!");
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    // Type
    {
      title: "TYPE",
      dataIndex: "campaignType",
      key: "campaignType",
      align: "center",
      ellipsis: true,
      render: (text) => <Tag color="blue">{text?.toUpperCase()}</Tag>,
    },
    // Status
    {
      title: "STATUS",
      dataIndex: "allstatus",
      key: "allstatus",
      align: "center",
      ellipsis: true,
      render: (text, record) => {
        if (text === "paused_balance") {
          return (
            <Tooltip title={record.failReason}>
              <Tag color="orange">PAUSED: NO BALANCE</Tag>
            </Tooltip>
          );
        }
        const tag = <Tag color={text === "failed" ? "red" : ""}>{text?.toUpperCase()}</Tag>;
        return record.failReason ? <Tooltip title={record.failReason}>{tag}</Tooltip> : tag;
      },
    },
    // Whatsapp Response Status
    // {
    //   title: "whatsappResponses",
    //   dataIndex: "whatsappResponses",
    //   key: "whatsappResponses",
    //   align: "center",
    //   ellipsis: true,
    //   render: (responses) => {
    //     if (!responses || responses.length === 0) {
    //       return <Tag color="default">NO STATUS</Tag>;
    //     }

    //     const lastResponse = responses[responses.length - 1];
    //     const status = lastResponse?.status || "Unknown";
    //     const error = lastResponse?.error || "";
    //     const text = error ? `${status} : ${error}` : status;

    //     let color = "blue";
    //     if (status === "Failed") color = "red";
    //     else if (status === "Sent") color = "green";
    //     else if (status === "Pending") color = "orange";

    //     return <Tag color={color}>{text}</Tag>;
    //   },
    // },
    {
      title: "whatsappResponses",
      dataIndex: "lastResponseStatus",
      key: "lastResponseStatus",
      align: "center",
      ellipsis: true,
      render: (lastResponseStatus) => {
        if (!lastResponseStatus) {
          return <Tag color="default">NO STATUS</Tag>;
        }

        const status = lastResponseStatus;

        let color = "blue";
        if (status === "Failed") color = "red";
        else if (status === "Sent") color = "green";
        else if (status === "Pending") color = "orange";

        return <Tag color={color}>{status}</Tag>; // ✅ Only status
      },
    },
    // Reciever
    {
      title: "RECIEVER",
      dataIndex: "recipientCount",
      key: "recipientCount",
      align: "center",
      ellipsis: true,
      render: (recipientCount) => <Tag color="green">{recipientCount || 0}</Tag>,
    },
    // Messsages
    {
      title: "MESSAGES",
      dataIndex: "messages",
      key: "messages",
      align: "center",
      ellipsis: true,
      render: (text) => <Tag color="">{text?.toUpperCase() || 1}</Tag>,
    },

    {
      title: "CREATED ON",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      ellipsis: true,
      render: (text) => {
        if (!text) return "N/A";
        const dateObj = new Date(text);
        const date = dateObj.toLocaleDateString("en-GB");
        const time = dateObj.toLocaleTimeString();
        return (
          <div style={{ textAlign: "center" }}>
            <div>{date}</div>
            <div>{time}</div>
          </div>
        );
      },
    },
    // Scheduled For
    {
      title: "SCHEDULED FOR",
      dataIndex: "scheduleDate",
      key: "scheduleDate",
      align: "center",
      ellipsis: true,
      render: (text) => {
        if (!text) return "N/A";
        const dateObj = new Date(text);
        const date = dateObj.toLocaleDateString("en-GB");
        const time = dateObj.toLocaleTimeString();
        return (
          <div style={{ textAlign: "center" }}>
            <div>{date}</div>
            <div>{time}</div>
          </div>
        );
      },
    },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchInput(e.target.value);
  };

  return (
    <div className="page-content w-100">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0 py-3">Campaign Scheduler</h4>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-xxl-12">
            <div id="contactList" className="card">
              <div className="card-header py-3">
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <div className="d-flex flex-wrap flex-grow-1 gap-2">
                    {/* Add Button */}

                    <form className="d-flex">
                      <input
                        className="form-control me-2"
                        type="search"
                        id="search"
                        placeholder="Search"
                        aria-label="Search"
                        value={searchInput}
                        onChange={handleSearch}
                      />
                    </form>
                  </div>
                  <div className="flex-shrink-0 d-flex align-items-center gap-2">
                    {sending && (
                      <Tooltip
                        title={
                          sending.activeSite === sending.thisSite
                            ? `Sending from ${sending.activeSite}`
                            : `Sending is handled by ${sending.activeSite}; this server (${sending.thisSite}) is on standby`
                        }
                      >
                        <Tag color={sending.paused ? "red" : "green"}>
                          {sending.paused ? "SENDING PAUSED" : "SENDING ON"}
                        </Tag>
                      </Tooltip>
                    )}
                    {sending && (
                      <button
                        className={sending.paused ? "btn btn-primary" : "btn btn-outline-danger"}
                        onClick={toggleSending}
                      >
                        {sending.paused ? "Resume sending" : "Pause all sending"}
                      </button>
                    )}
                    <button
                      className="btn btn-success"
                      onClick={handleCreateCampaign}
                    >
                      Create New Campaign
                    </button>
                  </div>
                </div>
                <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                  <div className="flex-shrink-0">
                    <div className="hstack text-nowrap gap-2 align-item-center"></div>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <Table
                  dataSource={data}
                  columns={columns}
                  rowKey="_id"
                  scroll={{ x: true }}
                  style={{ overflowX: data.length > 5 ? "scroll" : "hidden" }}
                  pagination={{
                    current: page,
                    pageSize,
                    total,
                    position: "bottomRight",
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "15", "20", "50", "100"],
                  }}
                  onChange={handleTableChange}
                  components={{
                    header: {
                      cell: ({ style, ...restProps }) => (
                        <th
                          {...restProps}
                          style={{
                            ...style,
                            backgroundColor: "#c6c6c6",
                            color: "black",
                            textAlign: "center",
                          }}
                        />
                      ),
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignScheduler;
